import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Search } from "lucide-react";
import { captureEvent } from "@/lib/analytics";
import { HOMEPAGE_DEMO_STACKS, type HomepageDemoStack } from "@/lib/homepageDemoPreview";
import { cn } from "@/lib/utils";

export function HomepageDemoPreview() {
  const [selectedId, setSelectedId] = useState<HomepageDemoStack["id"]>("data-platform");
  const selected =
    HOMEPAGE_DEMO_STACKS.find((stack) => stack.id === selectedId) ?? HOMEPAGE_DEMO_STACKS[0];

  return (
    <section
      className="overflow-hidden rounded-2xl bg-card ring-1 ring-border"
      aria-labelledby="demo-preview-heading"
    >
      <header className="grid gap-3 border-b border-border px-5 py-6 sm:px-7 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
        <div>
          <h2
            id="demo-preview-heading"
            className="max-w-2xl text-balance text-2xl font-semibold tracking-[-0.025em] text-foreground sm:text-3xl"
          >
            Find the one stack that deserves a closer look.
          </h2>
          <p className="mt-2 max-w-2xl text-pretty text-sm leading-6 text-muted-foreground sm:text-base">
            This example comparison puts three Terraform stacks against their recent successful
            runs. Pick a stack to see DeployCompare&apos;s assessment.
          </p>
        </div>
        <span className="w-fit text-sm font-medium text-muted-foreground">Example comparison</span>
      </header>

      <div className="grid lg:grid-cols-[minmax(19rem,0.8fr)_minmax(0,1.2fr)]">
        <div className="border-b border-border bg-surface-1/55 p-3 sm:p-4 lg:border-b-0 lg:border-r">
          <div className="grid gap-2" aria-label="Terraform stacks in the example">
            {HOMEPAGE_DEMO_STACKS.map((stack) => {
              const selectedStack = stack.id === selected.id;
              const needsReview = stack.status === "investigate";

              return (
                <button
                  key={stack.id}
                  type="button"
                  aria-pressed={selectedStack}
                  onClick={() => {
                    if (selectedStack) return;
                    captureEvent("landing_demo_stack_selected", { status: stack.status });
                    setSelectedId(stack.id);
                  }}
                  className={cn(
                    "group min-h-20 w-full rounded-xl px-4 py-3 text-left outline-none transition-colors focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-1",
                    selectedStack
                      ? "bg-surface-3 text-foreground"
                      : "text-muted-foreground hover:bg-surface-2/75 hover:text-foreground"
                  )}
                >
                  <span className="flex min-w-0 items-center gap-3">
                    <span
                      className={cn(
                        "grid h-8 w-8 shrink-0 place-items-center rounded-lg",
                        needsReview ? "bg-warning/15 text-warning" : "bg-success/12 text-success"
                      )}
                      aria-hidden="true"
                    >
                      {needsReview ? <Search className="h-4 w-4" /> : <Check className="h-4 w-4" />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                        <span className="font-mono text-sm font-semibold text-foreground">
                          {stack.name}
                        </span>
                        <span
                          className={cn(
                            "text-xs font-semibold",
                            needsReview ? "text-warning" : "text-success"
                          )}
                        >
                          {needsReview ? "Investigate" : "Routine"}
                        </span>
                      </span>
                      <span className="mt-1 block font-mono text-xs text-muted-foreground">
                        {stack.actions}
                      </span>
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          <p className="px-4 pb-1 pt-4 text-xs leading-5 text-muted-foreground">
            <span className="font-mono text-foreground">+</span> add ·{" "}
            <span className="font-mono text-foreground">~</span> change ·{" "}
            <span className="font-mono text-foreground">-</span> destroy ·{" "}
            <span className="font-mono text-foreground">R</span> replace
          </p>
        </div>

        <div
          key={selected.id}
          className="home-evidence-reveal grid content-between gap-8 p-5 sm:p-7 lg:p-9"
          data-testid="homepage-demo-detail"
        >
          <p className="sr-only" role="status" aria-live="polite" aria-atomic="true">
            {selected.name}: {selected.status === "investigate" ? "Investigate" : "Routine"}.{" "}
            {selected.headline}
          </p>
          <div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span
                className={cn(
                  "inline-flex items-center gap-2 text-sm font-semibold",
                  selected.status === "investigate" ? "text-warning" : "text-success"
                )}
              >
                <span
                  className={cn(
                    "h-2 w-2 rounded-full",
                    selected.status === "investigate" ? "bg-warning" : "bg-success"
                  )}
                  aria-hidden="true"
                />
                {selected.status === "investigate" ? "Investigate" : "Routine"}
              </span>
              <span className="text-sm font-medium text-muted-foreground">
                {selected.rangeLabel}
              </span>
            </div>

            <h3 className="mt-5 max-w-xl text-balance text-2xl font-semibold tracking-[-0.025em] text-foreground sm:text-3xl">
              {selected.headline}
            </h3>
            <p className="mt-3 max-w-2xl text-pretty text-base leading-7 text-muted-foreground">
              {selected.explanation}
            </p>

            <div className="mt-8" aria-label={`Baseline and candidate scores for ${selected.name}`}>
              <div className="grid grid-cols-[minmax(7rem,auto)_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-4 text-sm">
                <span className="text-muted-foreground">Recent baseline</span>
                <span className="h-2 overflow-hidden rounded-full bg-surface-3">
                  <span
                    className="block h-full rounded-full bg-muted-foreground/55 transition-[width] duration-500 ease-out motion-reduce:transition-none"
                    style={{ width: `${Math.max(12, (selected.baselineScore / 16) * 100)}%` }}
                  />
                </span>
                <span className="font-mono text-xs text-muted-foreground">
                  {selected.baselineScore.toFixed(2)}
                </span>

                <span className="font-medium text-foreground">Candidate</span>
                <span className="h-2 overflow-hidden rounded-full bg-surface-3">
                  <span
                    className={cn(
                      "block h-full rounded-full transition-[width] duration-700 ease-out motion-reduce:transition-none",
                      selected.status === "investigate" ? "bg-warning" : "bg-success"
                    )}
                    style={{ width: `${Math.max(12, (selected.candidateScore / 16) * 100)}%` }}
                  />
                </span>
                <span className="font-mono text-xs font-semibold text-foreground">
                  {selected.candidateScore.toFixed(2)}
                </span>
              </div>
            </div>

            <dl className="mt-8 divide-y divide-border border-y border-border text-sm">
              <div className="grid gap-1 py-3 sm:grid-cols-[9rem_minmax(0,1fr)] sm:gap-4">
                <dt className="text-muted-foreground">Recent pattern</dt>
                <dd className="text-foreground">{selected.baseline}</dd>
              </div>
              <div className="grid gap-1 py-3 sm:grid-cols-[9rem_minmax(0,1fr)] sm:gap-4">
                <dt className="text-muted-foreground">Candidate plan</dt>
                <dd className="text-foreground">{selected.changeSummary}</dd>
              </div>
              <div className="grid gap-1 py-3 sm:grid-cols-[9rem_minmax(0,1fr)] sm:gap-4">
                <dt className="text-muted-foreground">Assessment</dt>
                <dd className="font-medium text-foreground">{selected.assessment}</dd>
              </div>
            </dl>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-4 border-t border-border pt-5">
            <p className="max-w-md text-sm leading-6 text-muted-foreground">
              The full demo lets you inspect the comparison, switch pipelines, and follow the
              evidence without connecting a repository.
            </p>
            <Link
              to="/demo"
              className={cn(
                "inline-flex min-h-11 items-center gap-2 rounded-lg bg-primary px-4 py-2.5",
                "text-sm font-semibold text-primary-foreground transition hover:brightness-110",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                "focus-visible:ring-offset-2 focus-visible:ring-offset-card"
              )}
              onClick={() =>
                captureEvent("landing_cta_clicked", {
                  cta: "view_demo",
                  location: "preview",
                })
              }
            >
              Try the live demo
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
